import { useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { parseCSV, generateCSV } from '../../../../lib/connectors';
import { useItemsStore } from '../../../../stores/prioritization/itemsStore';
import type { Item, Priority } from '../../../../types/prioritization/lifecycle';
import {
  summarizeImportOutcome,
  type ImportRowFailure,
  type ImportSaveOutcome,
} from '../../../../lib/importBatch';
import {
  EXPORT_COLUMNS,
  IMPORT_TEMPLATE_COLUMNS,
  IMPORT_TEMPLATE_EXAMPLE,
  orderFromImportedRanks,
  priorityToExportRow,
  readImportedRank,
  rowToImportItem,
} from './integrationMappers';
import type { ImportItemPayload, PriorityExportRecord, RankImportOptions } from './integrationTypes';

type Notify = (notification: {
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  duration?: number;
}) => void;

/** Excel/CSV import, export, and template download for the Integration Sync strip. */
export function useSpreadsheetSync({
  listName,
  priorities,
  onImportItems,
  onNotification,
  rankImport,
}: {
  listName: string;
  priorities: PriorityExportRecord[];
  onImportItems: (items: ImportItemPayload[]) => Promise<ImportSaveOutcome>;
  onNotification: Notify;
  /** Without it, a file's `rank` column changes nothing. */
  rankImport?: RankImportOptions;
}) {
  // Item store access for round-trip update support
  const getItemById = useItemsStore((s) => s.getItemById);
  const updateItem  = useItemsStore((s) => s.updateItem);

  // Excel/CSV: hidden file input
  const csvExcelInputRef = useRef<HTMLInputElement>(null);

  // Excel/CSV import: parse file, route rows with an id to update, others to create
  const handleExcelCsvFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);
      const isCsv = /\.csv$/i.test(file.name);
      if (!isExcel && !isCsv) {
        onNotification({ type: 'error', title: 'Unsupported file', message: 'Please choose a .csv, .xlsx, or .xls file.' });
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const content = reader.result as string | ArrayBuffer;
          let rows: Record<string, string | number | undefined>[];
          const source = isExcel ? 'excel' : 'csv';
          if (isExcel && typeof content !== 'string') {
            const wb = XLSX.read(content, { type: 'array' });
            const firstSheet = wb.Sheets[wb.SheetNames[0]];
            rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' }) as Record<string, string | number | undefined>[];
          } else {
            const text = typeof content === 'string' ? content : new TextDecoder().decode(content as ArrayBuffer);
            const { rows: parsed } = parseCSV(text, { hasHeaders: true });
            rows = parsed;
          }
          const parsedRows = rows.map((row, i) => ({ item: rowToImportItem(row, i, source), rank: readImportedRank(row) }));
          const parsed = parsedRows.map((r) => r.item).filter((item) => item.title);
          if (parsed.length === 0) {
            onNotification({ type: 'info', title: 'No items', message: 'No rows with a title/name column were found.' });
            return;
          }

          // Split into update vs create
          const toCreate: typeof parsed = [];
          let updated = 0;
          let statusChangesNotApplied = 0;
          const updateFailures: ImportRowFailure[] = [];

          for (const [rowIndex, row] of parsed.entries()) {
            if (row.id) {
              const existing = getItemById(row.id);
              if (existing) {
                // Build update payload — only include fields that differ and are valid
                const updates: Record<string, unknown> = {};
                if (row.title && row.title !== existing.title) updates.title = row.title;
                if (row.description !== undefined && row.description !== existing.description) updates.description = row.description;
                if (row.itemCategory && row.itemCategory !== existing.itemCategory) updates.itemCategory = row.itemCategory;
                const existingExtra = existing as unknown as Record<string, unknown>;
                if (row.metaCategory && row.metaCategory !== existingExtra.metaCategory) updates.metaCategory = row.metaCategory;
                if (row.strategicBucket && row.strategicBucket !== existingExtra.strategicBucket) updates.strategicBucket = row.strategicBucket;
                if (row.altitudeLevel !== undefined && row.altitudeLevel !== existing.altitudeLevel) updates.altitudeLevel = row.altitudeLevel;
                // Never exit status: an import doesn't defer or archive items.
                // That happens on the item list, where archiving asks first.
                if (row.exit_status && row.exit_status !== existingExtra.exit_status) statusChangesNotApplied++;
                if (row.work_type && row.work_type !== existingExtra.work_type) updates.work_type = row.work_type;
                if (row.tags && JSON.stringify(row.tags) !== JSON.stringify(existing.tags)) updates.tags = row.tags;
                // Never `source`: row.source is the file's type ('csv' / 'excel'),
                // not where the item came from, and rewriting it made an
                // unchanged row a write the server refuses on a ranked item.

                if (Object.keys(updates).length > 0) {
                  try {
                    updateItem(row.id, updates as Partial<Item | Priority>);
                    updated++;
                  } catch (err) {
                    updateFailures.push({
                      rowNumber: rowIndex + 1,
                      title: row.title,
                      reason: err instanceof Error ? err.message : 'The update could not be saved',
                    });
                  }
                } else {
                  updated++; // row matched, nothing to change
                }
                continue;
              }
            }
            toCreate.push(row);
          }

          const outcome: ImportSaveOutcome =
            toCreate.length > 0 ? await onImportItems(toCreate) : { created: 0, failures: [] };

          onNotification(
            summarizeImportOutcome({
              created: outcome.created,
              updated,
              skipped: outcome.skipped,
              failures: [...outcome.failures, ...updateFailures],
              label: `Import of ${file.name}`,
              successTitle: 'Import complete',
            }),
          );

          if (statusChangesNotApplied > 0) {
            onNotification({
              type: 'info',
              title: 'Status changes not applied',
              message: `${statusChangesNotApplied === 1 ? '1 row changes' : `${statusChangesNotApplied} rows change`} an item's status. An import doesn't defer or archive items; do that from the item list, where archiving asks you to confirm.`,
              duration: 8000,
            });
          }

          // The file's ranks reorder the list only through rankImport, and
          // only when its gate allows (owner ruling 2026-11-02, poolAuthority).
          if (rankImport) {
            const currentIds = [...priorities].sort((a, b) => a.rank - b.rank).map((p) => p.id);
            const members = new Set(currentIds);
            const ranks = new Map<string, number>();
            for (const { item, rank } of parsedRows) {
              if (item.id && rank !== null && members.has(item.id)) ranks.set(item.id, rank);
            }
            const nextOrder = orderFromImportedRanks(currentIds, ranks);
            if (ranks.size > 0 && nextOrder.some((id, i) => id !== currentIds[i])) {
              if (rankImport.gate.allowed) {
                rankImport.apply(nextOrder);
              } else {
                onNotification({
                  type: 'warning',
                  title: 'Ranks not applied',
                  message: rankImport.gate.reason ?? 'The ranks in the file were not applied.',
                  duration: 8000,
                });
              }
            }
          }
        } catch (err) {
          onNotification({
            type: 'error',
            title: 'Import failed',
            message: err instanceof Error ? err.message : 'Could not parse file.',
          });
        }
      };
      if (isExcel) reader.readAsArrayBuffer(file);
      else reader.readAsText(file, 'UTF-8');
    },
    [onImportItems, onNotification, getItemById, updateItem, priorities, rankImport]
  );

  // Excel/CSV export: every export column, in order
  const getExportRows = useCallback((): Record<string, string | number>[] => {
    if (priorities.length === 0) return [];
    const columnOrder = [...EXPORT_COLUMNS];
    return priorities.map((p) => {
      const flat = priorityToExportRow(p);
      const out: Record<string, string | number> = {};
      for (const k of columnOrder) out[k] = flat[k] ?? '';
      return out;
    });
  }, [priorities]);

  // Excel/CSV export: CSV (same columns, same order as the Excel export)
  const handleExportCsv = useCallback(() => {
    if (priorities.length === 0) {
      onNotification({ type: 'info', title: 'Nothing to export', message: 'Add priorities to the list first.' });
      return;
    }
    const exportData = getExportRows();
    const fields = [...EXPORT_COLUMNS];
    const csvContent = generateCSV(exportData, { fields, includeHeaders: true });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${listName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    onNotification({ type: 'success', title: 'CSV exported', message: `Exported ${priorities.length} priorities (${fields.length} columns).` });
  }, [priorities.length, listName, onNotification, getExportRows]);

  // Excel/CSV export: Excel
  const handleExportExcel = useCallback(() => {
    if (priorities.length === 0) {
      onNotification({ type: 'info', title: 'Nothing to export', message: 'Add priorities to the list first.' });
      return;
    }
    const rows = getExportRows();
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Priorities');
    const filename = `${listName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    onNotification({ type: 'success', title: 'Excel exported', message: `Exported ${priorities.length} priorities (${rows[0] ? Object.keys(rows[0]).length : 0} columns).` });
  }, [priorities.length, listName, onNotification, getExportRows]);

  // Download import template (CSV and Excel) so others can fill it and re-import
  const handleDownloadImportTemplateCsv = useCallback(() => {
    const headers = [...IMPORT_TEMPLATE_COLUMNS];
    const exampleRow = headers.map((h) => IMPORT_TEMPLATE_EXAMPLE[h] ?? '');
    const csvContent = generateCSV([Object.fromEntries(headers.map((h, i) => [h, exampleRow[i]]))], {
      fields: headers,
      includeHeaders: true,
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'priorities_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    onNotification({ type: 'success', title: 'Template downloaded', message: 'The template has the ' + headers.length + ' columns an import reads. Fill at least title, then use Import from Excel/CSV.' });
  }, [onNotification]);

  const handleDownloadImportTemplateExcel = useCallback(() => {
    const headers = [...IMPORT_TEMPLATE_COLUMNS];
    const exampleRow = headers.map((h) => IMPORT_TEMPLATE_EXAMPLE[h] ?? '');
    const data = [Object.fromEntries(headers.map((h, i) => [h, exampleRow[i]]))];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Priorities');
    const instructions = [
      ['Priorities import template'],
      [],
      ['These are the columns an import reads. Fill in what you have; leave the rest blank.'],
      ['Required: title. Rows with an id update that item; rows without one create a new item.'],
      [],
      ['Column', '', ''],
      ['id', 'Optional', 'An existing item\'s id, to update it. Leave blank to create a new item.'],
      ['title', 'Required', 'Short name of the item.'],
      ['description', 'Optional', 'Longer description or context.'],
      ['rank', 'Optional', 'A number. Reorders an item pool only if you may: in a cycle, the cycle owner; elsewhere, whoever may reorder the pool.'],
      ['itemCategory', 'Optional', 'problem, opportunity, risk, solution, commitment or investment.'],
      ['metaCategory', 'Optional', 'people, things, places, activities, information or time.'],
      ['strategicBucket', 'Optional', 'growth, commitments, reliability, platform or compliance.'],
      ['altitudeLevel', 'Optional', '1 to 5.'],
      ['tags', 'Optional', 'Labels separated by commas, semicolons or |.'],
      ['work_type', 'Optional', 'Kind of work, in your own words.'],
      [],
      ['An import never defers or archives items; do that from the item list.'],
      ['Save the Priorities sheet, then use "Import from Excel/CSV" in the app.'],
    ];
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
    wsInstructions['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 52 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
    XLSX.writeFile(wb, 'priorities_import_template.xlsx');
    onNotification({ type: 'success', title: 'Template downloaded', message: 'The template has the ' + headers.length + ' columns an import reads. Fill at least title, then use Import from Excel/CSV.' });
  }, [onNotification]);

  return {
    csvExcelInputRef,
    handleExcelCsvFileSelected,
    handleExportCsv,
    handleExportExcel,
    handleDownloadImportTemplateCsv,
    handleDownloadImportTemplateExcel,
  };
}
